/**
 * Run Matrix generator (Roads & Sewers)
 * =====================================
 *
 * The Groundworks side of the app has a **Plot Matrix** — one row per plot, one
 * column per cost category, every cell a £ amount, so an estimator can see what
 * each plot costs and where the money sits. Roads & Sewers has no plots; its
 * natural unit of measure is the **drainage run** (manhole-to-manhole pipe
 * length). This module is the Run Matrix equivalent: one row per pipe run
 * (Foul + Storm), one column per drainage cost category.
 *
 * ## Where the numbers come from
 *
 * Nothing is re-calculated here. The matrix is derived from the **same** two
 * builders the Foul Sewers / Storm Sewers Bill Summary tabs render from —
 * `buildFoulSummaryData` and `buildStormSummaryData` — priced with the same
 * `evaluateCode → nettRate` item-library map. Every leaf item of those trees is
 * classified into a matrix column and allocated to one or more runs, so:
 *
 *   Σ matrix cells + Σ unallocated = Foul Bill Summary total + Storm Bill Summary total
 *
 * holds by construction. `RunMatrixResult.reconciliation` exposes both sides of
 * that identity so the UI can prove it rather than assert it.
 *
 * ## How an item is attributed to a run
 *
 * In priority order (see `allocateRow`):
 *  1. **Direct id match** — the item's key IS a pipe run / manhole / lateral /
 *     gully id (that's how the builders key their per-entity rows).
 *  2. **Formula attribution** — the item carries `formulaInfo.rows`, i.e. the
 *     per-source breakdown the Bill Summary's info-icon drawer already shows
 *     (`{ ref: 'MH1–MH2', value: 2 }`). The amount is split pro-rata by those
 *     values, each `ref` resolved to a run.
 *  3. **Site-wide** — manually-entered aggregate items (EO items, redundant
 *     sewer, Clegg testing, road-construction credits …) belong to no single
 *     run, so they land in that discipline's "Not run-specific" row. They are
 *     part of the grand total, never silently dropped.
 *
 * Manholes are owned by the run they head (`fromMH`), falling back to the run
 * that arrives at them (`toMH`) for terminal chambers — so each chamber is
 * counted exactly once. Laterals / gullies follow their own connection fields
 * (`toPipeRun` → `toManhole` → `toLateral`) and only fall back to the
 * positional `pipeRuns[i]` pairing the take-off sheets themselves use.
 */

import type {
  GullyConnection,
  LateralConnection,
  Manhole,
  ManholeScheduleConfig,
  SewerPipeRun,
  StormSewerRun,
} from '../types/tender.types';
import { buildFoulSummaryData } from './foulSummaryUtils';
import { buildStormSummaryData } from './stormSummaryUtils';

// ── Public types ─────────────────────────────────────────────────────────────

export type RunMatrixDiscipline = 'foul' | 'storm';

export const RUN_MATRIX_DISCIPLINE_LABEL: Record<RunMatrixDiscipline, string> = {
  foul: 'Foul Sewers',
  storm: 'Storm Sewers',
};

/** One matrix column. `group` renders as the parent header cell. */
export interface RunMatrixColumn {
  key: string;
  group: string;
  label: string;
  /** Shown in the column tooltip and in the "How allocation works" modal. */
  description: string;
}

export const RUN_MATRIX_COLUMNS: readonly RunMatrixColumn[] = [
  {
    key: 'runs',
    group: 'Pipework',
    label: 'Pipe Runs',
    description:
      'The run itself — excavate, lay, bed & surround, backfill and cart surplus, priced at the run\'s length × its Ø / backfill / depth-band rate.',
  },
  {
    key: 'stub',
    group: 'Pipework',
    label: 'Rockers / Stubs',
    description:
      'Extra-over for rocker & stub pipes, taken from each run\'s Rockers column and split by pipe material and diameter.',
  },
  {
    key: 'junctions',
    group: 'Pipework',
    label: 'Junctions',
    description:
      'Junctions, saddles and core drilling, taken from each run\'s Junctions column and sized against the connecting lateral / gully diameter.',
  },
  {
    key: 'concProt',
    group: 'Pipework',
    label: 'Conc. Bed & Protection',
    description:
      'Extra-over concrete bed & surround and concrete protection slabs. Protection slabs come off each run\'s own Concrete Protection column; the bed & surround rows are manual site-wide entries.',
  },
  {
    key: 'manholes',
    group: 'Structures',
    label: 'Chambers',
    description:
      'Manhole chambers, each priced on Ø, chamber type and depth-to-invert band. A chamber is charged to the run it heads (its From MH), or to the run arriving at it if it is a terminal chamber.',
  },
  {
    key: 'mhExtras',
    group: 'Structures',
    label: 'MH Extras',
    description:
      'Ladders, safety chains, AMPS, backdrops, muckstoppers, filter drain, Clegg testing and the road-construction credits. Per-manhole extras follow their chamber\'s run; manual aggregate rows are site-wide.',
  },
  {
    key: 'laterals',
    group: 'Connections',
    label: 'House Laterals',
    description:
      'House lateral connections plus their NME / 1200mmØ chamber and cover extras, charged to the run each lateral connects into.',
  },
  {
    key: 'gullies',
    group: 'Connections',
    label: 'Gully Laterals',
    description:
      'Storm gully laterals, road gullies, rockers into manhole, GulliBloks and gully sundries, charged to the run each gully connects into.',
  },
  {
    key: 'eo',
    group: 'Extra Over',
    label: 'EO & Sundries',
    description:
      'Extra-over items and removal of redundant sewer — working in offsite carriageway, trench reinstatement, breaking into existing manholes, geotextile wrap and similar. These are manual take-off quantities and are almost always site-wide rather than run-specific.',
  },
] as const;

/** One priced item (or pro-rata share of one) behind a matrix cell. */
export interface RunMatrixCellLine {
  description: string;
  qty: number;
  unit: string;
  rate: number;
  amount: number;
  /** Set when the line is a pro-rata share of an aggregated Bill Summary item. */
  share?: number;
  /** Bill Summary group path the item sits in, e.g. "Drainage Runs › Junctions". */
  source: string;
  evaluateCode?: string;
}

export interface RunMatrixCell {
  total: number;
  lines: RunMatrixCellLine[];
}

export type RunMatrixRowKind = 'run' | 'site-wide' | 'separator';

export interface RunMatrixRow {
  key: string;
  rowKind: RunMatrixRowKind;
  discipline: RunMatrixDiscipline;
  /** Run reference ("MH1-MH2"), or the section title on a separator row. */
  label: string;
  fromMH?: string;
  toMH?: string;
  length: number;
  diameter: string;
  pipeType?: string;
  /** Run depth category (depth to invert band), in metres. */
  depth: number;
  cells: Record<string, RunMatrixCell>;
  total: number;
}

export interface RunMatrixReconciliation {
  /** Σ of every priced leaf item in the discipline's Bill Summary tree. */
  billTotal: number;
  /** Σ of the discipline's run rows. */
  allocated: number;
  /** Σ of the discipline's "Not run-specific" row. */
  siteWide: number;
  /** billTotal − (allocated + siteWide). Zero unless something is wrong. */
  variance: number;
}

export interface RunMatrixResult {
  columns: readonly RunMatrixColumn[];
  /** Display order, separator rows included. */
  rows: RunMatrixRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
  runCount: number;
  totalLength: number;
  disciplineTotals: Record<RunMatrixDiscipline, number>;
  reconciliation: Record<RunMatrixDiscipline, RunMatrixReconciliation>;
  /** Effective markup factor the amounts were priced at (1 = net). */
  markupFactor: number;
  /** Priced items that could not be tied to a run, for the allocation modal. */
  siteWideItems: Array<{
    discipline: RunMatrixDiscipline;
    columnKey: string;
    description: string;
    amount: number;
  }>;
}

export interface RunMatrixDisciplineInput {
  /** Storm runs (`StormSewerRun`) widen this — they extend `SewerPipeRun`. */
  pipeRuns: SewerPipeRun[];
  manholes: Manhole[];
  laterals: LateralConnection[];
  /** Storm only. */
  gullies?: GullyConnection[];
  /** Manual EO / adjustment quantities (`pipeRuns.config.eoQtys`). */
  eoQtys?: Record<string, number>;
  manholeScheduleConfig?: ManholeScheduleConfig;
}

export interface RunMatrixInput {
  foul?: RunMatrixDisciplineInput;
  storm?: RunMatrixDisciplineInput;
  /** `resourceCode → nettRate`, same map the Bill Summary tabs build. */
  rateMap: Record<string, number>;
  /** `(1 + ohp%) × (1 + prelims%)`. Defaults to 1 (net, ties to Bill Summary). */
  markupFactor?: number;
}

// ── Internal shape shared by both summary trees ───────────────────────────────
// FoulSummaryRow and StormSummaryRow are structurally identical for everything
// this module reads, so one local interface covers both without either util
// having to depend on the other.

interface SummaryTreeRow {
  key: string;
  reference?: string;
  description?: string;
  quantity?: string;
  unit?: string;
  evaluateCode?: string;
  formulaInfo?: { rows: Array<{ ref: string; value: number; unit: string }> };
  children?: SummaryTreeRow[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Refs appear as "MH1-MH2", "MH1–MH2" (en dash) and with stray spacing. */
const normalizeRef = (ref: string | undefined): string =>
  (ref ?? '')
    .replace(/[‐-―]/g, '-')
    .replace(/\s+/g, '')
    .toUpperCase();

const parseQty = (raw: string | undefined): number => {
  if (raw == null || raw === '') return 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
};

// `parseFloat(n.toFixed(2))`, NOT `Math.round(n * 100) / 100` — the two are not
// interchangeable for floating-point input: e.g. `Math.round(15.555 * 100) / 100`
// is `15.56` but `(15.555).toFixed(2)` is `"15.55"` (binary floating-point can't
// represent 15.555 exactly, and the two operations round that stored value
// differently). Every R&S rateMap in the app rounds via `.toFixed(2)`
// (`FoulSewersBillSummary.tsx`, `RoadsBillSummary.tsx`,
// `useRoadsAndSewersTotalSync.ts`, `PrelimsBillSummary.tsx`, `prelimsMapping.ts`)
// — this one used the other method and, across enough rows, drifted the Run
// Matrix's nett-section totals (Pump Station & Rising Main included) a few
// pence away from their own Bill Summary tab even after every other rounding
// fix here.
const round2 = (n: number): number => parseFloat(n.toFixed(2));

/**
 * Applies a markup factor to an item-library rate the way Sum Analysis and every
 * bill summary do: baked into the rate at 2dp *before* extension, so a marked-up
 * matrix stays penny-exact against a marked-up bill rather than drifting by a
 * rounding step. Shared with `siteWorksMatrixGenerator.ts`.
 *
 * Always rounds, even at `markupFactor: 1` (every nett section — Drainage Runs,
 * Roads, Main Services, Substation, POS, Compound, the Foul/Storm build-ups
 * including Pump Station/Rising Main — forces exactly this). Every R&S Bill
 * Summary's own rateMap rounds the raw item-library rate to 2dp *before* any
 * multiplication, markup or not (`parseFloat((item.nettRate || 0).toFixed(2))`,
 * e.g. `FoulSewersBillSummary.tsx`, `RoadsBillSummary.tsx`,
 * `useRoadsAndSewersTotalSync.ts`) — a raw rate can carry more than 2dp (e.g.
 * 2107.678), and skipping the round here at factor 1 (as this used to) left
 * that extra precision in the matrix's Amount while the bill's own Rate column
 * had already discarded it, a few pence apart depending on quantity.
 */
export const markedUpRate = (rate: number, markupFactor: number): number =>
  round2(rate * markupFactor);

/**
 * Bill Summary group keys that map onto a matrix column, checked
 * **deepest-ancestor-first** so a nested group (e.g. Junctions ›
 * Concrete Protection Slab) wins over its parent. Foul and Storm use different
 * key prefixes for the same concepts, so both appear here.
 */
const GROUP_KEY_TO_COLUMN: Record<string, string> = {
  // Extra-over concrete, nested under Junctions in both disciplines
  'fjunc-eo-conc-bed': 'concProt',
  'fjunc-conc-protection': 'concProt',
  'seo-conc-bed': 'concProt',
  'seo-conc-protect': 'concProt',
  // Pipework
  'stub-pipes': 'stub',
  junctions: 'junctions',
  // EO / sundries
  'eo-items': 'eo',
  'seo-misc': 'eo',
  'seo-redundant': 'eo',
  'redundant-sewer': 'eo',
  'filter-drain': 'mhExtras',
  // Connections
  'lateral-connections': 'laterals',
  'storm-laterals': 'laterals',
  'gully-laterals': 'gullies',
  // Structures / runs — least specific, so they only match as a last resort
  manholes: 'manholes',
  'drainage-runs': 'runs',
};

// ── Run registry ─────────────────────────────────────────────────────────────

interface RunEntry {
  row: RunMatrixRow;
  /** Positional index within its discipline's pipeRuns table. */
  index: number;
}

interface DisciplineIndex {
  discipline: RunMatrixDiscipline;
  runs: RunEntry[];
  /** Any recognised textual/id reference → run key. */
  refToRunKey: Map<string, string>;
  /** Bill Summary row key of a per-entity item → run key. */
  itemKeyToRunKey: Map<string, string>;
  siteWideRow: RunMatrixRow;
  billTotal: number;
}

const emptyCells = (): Record<string, RunMatrixCell> => {
  const cells: Record<string, RunMatrixCell> = {};
  RUN_MATRIX_COLUMNS.forEach((col) => {
    cells[col.key] = { total: 0, lines: [] };
  });
  return cells;
};

const makeRow = (
  key: string,
  rowKind: RunMatrixRowKind,
  discipline: RunMatrixDiscipline,
  label: string,
  extras: Partial<RunMatrixRow> = {},
): RunMatrixRow => ({
  key,
  rowKind,
  discipline,
  label,
  length: 0,
  diameter: '',
  depth: 0,
  cells: emptyCells(),
  total: 0,
  ...extras,
});

/**
 * Builds the per-discipline lookup tables the allocator needs: one row per pipe
 * run, plus maps from every reference form the Bill Summary tree can hand us
 * (run id, run ref, "From-To", manhole ref, lateral id, gully ref) back to a
 * run key.
 */
const buildDisciplineIndex = (
  discipline: RunMatrixDiscipline,
  input: RunMatrixDisciplineInput,
): DisciplineIndex => {
  const pipeRuns = input.pipeRuns;
  const runs: RunEntry[] = pipeRuns.map((run, index) => {
    const key = `${discipline}-run-${index}`;
    const refLabel = run.fromMH || run.toMH ? `${run.fromMH ?? ''}-${run.toMH ?? ''}` : '';
    return {
      index,
      row: makeRow(key, 'run', discipline, run.ref || refLabel || `Run ${index + 1}`, {
        fromMH: run.fromMH,
        toMH: run.toMH,
        length: run.length || 0,
        diameter: run.diameter || '',
        pipeType: run.pipeType,
        depth: run.runDepthCategory || run.depthCategory || 0,
      }),
    };
  });

  const refToRunKey = new Map<string, string>();
  const itemKeyToRunKey = new Map<string, string>();
  const addRef = (ref: string | undefined, runKey: string): void => {
    const norm = normalizeRef(ref);
    if (norm && !refToRunKey.has(norm)) refToRunKey.set(norm, runKey);
  };

  pipeRuns.forEach((run, index) => {
    const runKey = runs[index].row.key;
    addRef(run.id, runKey);
    addRef(run.ref, runKey);
    addRef(`${run.fromMH ?? ''}-${run.toMH ?? ''}`, runKey);
    // The builders key each drainage-run item by `run.id || run-${i}`.
    itemKeyToRunKey.set(run.id || `run-${index}`, runKey);
  });

  // Manhole ownership: a chamber belongs to the run it heads; terminal chambers
  // fall back to the run that arrives at them. Two passes so a chamber that is
  // both a From and a To always prefers the run it heads.
  const manholeOwner = new Map<string, string>();
  pipeRuns.forEach((run, index) => {
    const norm = normalizeRef(run.fromMH);
    if (norm && !manholeOwner.has(norm)) manholeOwner.set(norm, runs[index].row.key);
  });
  pipeRuns.forEach((run, index) => {
    const norm = normalizeRef(run.toMH);
    if (norm && !manholeOwner.has(norm)) manholeOwner.set(norm, runs[index].row.key);
  });
  manholeOwner.forEach((runKey, norm) => {
    if (!refToRunKey.has(norm)) refToRunKey.set(norm, runKey);
  });
  input.manholes.forEach((mh, index) => {
    const owner = manholeOwner.get(normalizeRef(mh.ref));
    if (owner) {
      itemKeyToRunKey.set(mh.id || `${discipline === 'foul' ? 'mh' : 'smh'}-${index}`, owner);
    }
  });

  // Laterals: follow the connection fields first, positional pairing last —
  // the take-off sheets pair pipeRuns[i] with laterals[i], so it is the
  // documented fallback rather than a guess.
  const lateralRunKey = new Map<string, string>();
  input.laterals.forEach((lat, index) => {
    const resolved =
      (lat.toPipeRun ? refToRunKey.get(normalizeRef(lat.toPipeRun)) : undefined) ??
      (lat.toManhole ? manholeOwner.get(normalizeRef(lat.toManhole)) : undefined) ??
      runs[index]?.row.key;
    if (!resolved) return;
    const itemKey = lat.id || `${discipline === 'foul' ? 'lat' : 'slat'}-${index}`;
    itemKeyToRunKey.set(itemKey, resolved);
    lateralRunKey.set(normalizeRef(lat.ref || lat.id), resolved);
    if (lat.ref) addRef(lat.ref, resolved);
  });

  // Gullies (Storm only): same idea, with the extra "connects to a lateral" hop.
  (input.gullies ?? []).forEach((gully, index) => {
    const resolved =
      (gully.toPipeRun ? refToRunKey.get(normalizeRef(gully.toPipeRun)) : undefined) ??
      (gully.toManhole ? manholeOwner.get(normalizeRef(gully.toManhole)) : undefined) ??
      (gully.toLateral ? lateralRunKey.get(normalizeRef(gully.toLateral)) : undefined) ??
      runs[index]?.row.key;
    if (!resolved) return;
    itemKeyToRunKey.set(gully.id || `sgully-${index}`, resolved);
    addRef(gully.gullyRef, resolved);
    addRef(gully.runRef, resolved);
  });

  return {
    discipline,
    runs,
    refToRunKey,
    itemKeyToRunKey,
    siteWideRow: makeRow(
      `${discipline}-site-wide`,
      'site-wide',
      discipline,
      'Not run-specific',
    ),
    billTotal: 0,
  };
};

// ── Allocation ───────────────────────────────────────────────────────────────

const classifyColumn = (ancestorKeys: string[], itemKeyIsManhole: boolean): string => {
  for (let i = ancestorKeys.length - 1; i >= 0; i -= 1) {
    const mapped = GROUP_KEY_TO_COLUMN[ancestorKeys[i]];
    if (!mapped) continue;
    // The Manholes section holds both the chamber rows and every per-manhole
    // extra; only a row keyed by an actual manhole is a chamber.
    if (mapped === 'manholes' && !itemKeyIsManhole) return 'mhExtras';
    return mapped;
  }
  return 'eo';
};

const pushLine = (
  row: RunMatrixRow,
  columnKey: string,
  line: RunMatrixCellLine,
): void => {
  const cell = row.cells[columnKey] ?? { total: 0, lines: [] };
  cell.total += line.amount;
  cell.lines.push(line);
  row.cells[columnKey] = cell;
  row.total += line.amount;
};

/**
 * Prices one Bill Summary leaf item and pushes it (whole, or split pro-rata)
 * onto the matching run rows.
 */
const allocateRow = (
  item: SummaryTreeRow,
  ancestorKeys: string[],
  ancestorLabels: string[],
  index: DisciplineIndex,
  rowsByKey: Map<string, RunMatrixRow>,
  manholeItemKeys: Set<string>,
  rateMap: Record<string, number>,
  markupFactor: number,
  siteWideItems: RunMatrixResult['siteWideItems'],
): number => {
  const qty = parseQty(item.quantity);
  if (qty === 0) return 0;

  // Exact match only, deliberately — mirrors FoulSewersBillSummary.tsx's own
  // `rateMap[row.evaluateCode] ?? 0` (no trim, no case normalization), so a row
  // this can't price is a row the real Bill Summary can't price either. A
  // fuzzier match here would resolve rates the bill treats as unpriced,
  // inflating this matrix's total above the bill's rather than tying to it.
  const baseRate = item.evaluateCode ? rateMap[item.evaluateCode] ?? 0 : 0;
  if (baseRate === 0) return 0;
  // Markup is baked into the rate at 2dp before multiplying out — the same
  // order Sum Analysis and the bill summaries use, so totals stay penny-exact
  // against them rather than drifting by a rounding step.
  const rate = markedUpRate(baseRate, markupFactor);
  const amount = qty * rate;
  if (amount === 0) return 0;

  const columnKey = classifyColumn(ancestorKeys, manholeItemKeys.has(item.key));
  const source = ancestorLabels.filter(Boolean).join(' › ');
  const description = item.description || item.reference || item.key;

  const addTo = (
    runKey: string | undefined,
    lineQty: number,
    lineAmount: number,
    share?: number,
  ): void => {
    const target = (runKey && rowsByKey.get(runKey)) || index.siteWideRow;
    if (target === index.siteWideRow) {
      siteWideItems.push({
        discipline: index.discipline,
        columnKey,
        description,
        amount: lineAmount,
      });
    }
    pushLine(target, columnKey, {
      description,
      qty: lineQty,
      unit: item.unit ?? '',
      rate,
      amount: lineAmount,
      share,
      source,
      evaluateCode: item.evaluateCode,
    });
  };

  // 1 — the item IS a run / manhole / lateral / gully row.
  const directRunKey = index.itemKeyToRunKey.get(item.key);
  if (directRunKey) {
    addTo(directRunKey, qty, amount);
    return amount;
  }

  // 2 — the item carries a per-source formula breakdown; split it pro-rata.
  const formulaRows = item.formulaInfo?.rows ?? [];
  const weightTotal = formulaRows.reduce((sum, r) => sum + (r.value || 0), 0);
  if (formulaRows.length > 0 && weightTotal !== 0) {
    let allocatedAmount = 0;
    formulaRows.forEach((fRow, i) => {
      const weight = fRow.value || 0;
      if (weight === 0) return;
      const share = weight / weightTotal;
      const isLast = i === formulaRows.length - 1;
      // Give the last contributing line the residual so pro-rata rounding can
      // never lose or invent a penny against the Bill Summary amount.
      const lineAmount = isLast ? amount - allocatedAmount : amount * share;
      allocatedAmount += lineAmount;
      addTo(index.refToRunKey.get(normalizeRef(fRow.ref)), qty * share, lineAmount, share);
    });
    return amount;
  }

  // 3 — manual / aggregate item: site-wide.
  addTo(undefined, qty, amount);
  return amount;
};

const walkTree = (
  rows: SummaryTreeRow[],
  ancestorKeys: string[],
  ancestorLabels: string[],
  index: DisciplineIndex,
  rowsByKey: Map<string, RunMatrixRow>,
  manholeItemKeys: Set<string>,
  rateMap: Record<string, number>,
  markupFactor: number,
  siteWideItems: RunMatrixResult['siteWideItems'],
): number => {
  let total = 0;
  rows.forEach((row) => {
    if (row.children && row.children.length > 0) {
      total += walkTree(
        row.children,
        [...ancestorKeys, row.key],
        [...ancestorLabels, row.reference ?? row.description ?? ''],
        index,
        rowsByKey,
        manholeItemKeys,
        rateMap,
        markupFactor,
        siteWideItems,
      );
      return;
    }
    total += allocateRow(
      row,
      ancestorKeys,
      ancestorLabels,
      index,
      rowsByKey,
      manholeItemKeys,
      rateMap,
      markupFactor,
      siteWideItems,
    );
  });
  return total;
};

// ── Entry point ──────────────────────────────────────────────────────────────

const hasDisciplineData = (input?: RunMatrixDisciplineInput): boolean =>
  !!input &&
  ((input.pipeRuns?.length ?? 0) > 0 ||
    (input.manholes?.length ?? 0) > 0 ||
    (input.laterals?.length ?? 0) > 0 ||
    (input.gullies?.length ?? 0) > 0);

export const buildRunMatrix = (input: RunMatrixInput): RunMatrixResult => {
  const markupFactor = input.markupFactor && input.markupFactor > 0 ? input.markupFactor : 1;
  const siteWideItems: RunMatrixResult['siteWideItems'] = [];
  const rows: RunMatrixRow[] = [];
  const disciplineTotals: Record<RunMatrixDiscipline, number> = { foul: 0, storm: 0 };
  const reconciliation: Record<RunMatrixDiscipline, RunMatrixReconciliation> = {
    foul: { billTotal: 0, allocated: 0, siteWide: 0, variance: 0 },
    storm: { billTotal: 0, allocated: 0, siteWide: 0, variance: 0 },
  };

  const disciplines: Array<{ discipline: RunMatrixDiscipline; data?: RunMatrixDisciplineInput }> = [
    { discipline: 'foul', data: input.foul },
    { discipline: 'storm', data: input.storm },
  ];

  disciplines.forEach(({ discipline, data }) => {
    if (!hasDisciplineData(data) || !data) return;

    const index = buildDisciplineIndex(discipline, data);
    const rowsByKey = new Map<string, RunMatrixRow>();
    index.runs.forEach((entry) => rowsByKey.set(entry.row.key, entry.row));

    const manholeItemKeys = new Set<string>(
      data.manholes.map(
        (mh, i) => mh.id || `${discipline === 'foul' ? 'mh' : 'smh'}-${i}`,
      ),
    );

    const tree: SummaryTreeRow[] =
      discipline === 'foul'
        ? (buildFoulSummaryData(
            data.pipeRuns,
            data.manholes,
            data.laterals,
            data.eoQtys,
            data.manholeScheduleConfig,
          ) as SummaryTreeRow[])
        : (buildStormSummaryData(
            data.pipeRuns as StormSewerRun[],
            data.manholes,
            data.laterals,
            data.gullies ?? [],
            data.eoQtys,
            data.manholeScheduleConfig,
          ) as SummaryTreeRow[]);

    const billTotal = walkTree(
      tree,
      [],
      [],
      index,
      rowsByKey,
      manholeItemKeys,
      input.rateMap,
      markupFactor,
      siteWideItems,
    );

    const runRows = index.runs.map((entry) => entry.row);
    const allocated = runRows.reduce((sum, row) => sum + row.total, 0);
    const siteWide = index.siteWideRow.total;

    reconciliation[discipline] = {
      billTotal,
      allocated,
      siteWide,
      variance: billTotal - (allocated + siteWide),
    };
    disciplineTotals[discipline] = allocated + siteWide;

    rows.push(
      makeRow(
        `${discipline}-separator`,
        'separator',
        discipline,
        RUN_MATRIX_DISCIPLINE_LABEL[discipline],
      ),
    );
    rows.push(...runRows);
    // Only show the site-wide row when it actually carries cost — an empty one
    // is noise, and its absence tells the estimator every penny is run-specific.
    if (Math.abs(siteWide) > 0.005) rows.push(index.siteWideRow);
  });

  const columnTotals: Record<string, number> = {};
  RUN_MATRIX_COLUMNS.forEach((col) => {
    columnTotals[col.key] = rows
      .filter((row) => row.rowKind !== 'separator')
      .reduce((sum, row) => sum + (row.cells[col.key]?.total ?? 0), 0);
  });

  const runRows = rows.filter((row) => row.rowKind === 'run');

  return {
    columns: RUN_MATRIX_COLUMNS,
    rows,
    columnTotals,
    grandTotal: rows
      .filter((row) => row.rowKind !== 'separator')
      .reduce((sum, row) => sum + row.total, 0),
    runCount: runRows.length,
    totalLength: runRows.reduce((sum, row) => sum + row.length, 0),
    disciplineTotals,
    reconciliation,
    markupFactor,
    siteWideItems,
  };
};

/** Cost per linear metre of run — the headline comparison figure per run. */
export const runCostPerMetre = (row: RunMatrixRow): number | null =>
  row.rowKind === 'run' && row.length > 0 ? row.total / row.length : null;
